import { api } from "api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { Metric } from "api/responses";

const numberFormat = Intl.NumberFormat(navigator.language, { style: "decimal" });
const dollarFormat = Intl.NumberFormat(navigator.language, {
    style: "currency",
    currency: "USD",
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0, // round to whole dollars
});

/** Represents a metric that has been processed for use in the application. */
export class FormattedMetric {
    /** The title of the metric. */
    public readonly title: string;

    /** The value of the metric, formatted for display. */
    public readonly value: string;

    /* True if the raw value of the metric is non-zero. */
    public readonly isNonZero: boolean;

    /* True if the raw value of the metric is null. */
    public readonly isNull: boolean;

    /** The  percentage change of the metric value compared to the previous period. */
    public readonly percentChange: number | null;

    /** The change in value compared to the previous period. */
    public readonly valueChange: number | null;

    constructor(result: Metric) {
        this.title = result.title;
        this.isNull = result.value === null;
        this.percentChange = result.percentChange;
        this.valueChange = result.valueChange;

        if (result.value === null) {
            this.value = "";
            this.isNonZero = false;
            return;
        }

        this.isNonZero = result.value > 0 || result.value < 0;

        if (result.isCurrency) {
            this.value = dollarFormat.format(result.value);
        } else {
            this.value = numberFormat.format(result.value);
        }
    }
}

/**
 * Client hook to query for payment transaction metrics.
 * @param startDateMs Optional timestamp to limit the start of the metric query.
 * @param endDateMs Optional timestamp to limit the end of the metric query.
 * @param isMarketing Optional flag to indicate if the metrics are for marketing purposes.
 * @returns React Query properties.
 */
export function useTransactionMetrics(startDateMs?: number, endDateMs?: number, isMarketing?: boolean) {
    return useQuery({
        queryKey: ["metrics", startDateMs, endDateMs, isMarketing],
        queryFn: async (context) => {
            const results = await api.provider.metrics(
                toDateTimeOffset(startDateMs),
                toDateTimeOffset(endDateMs),
                isMarketing,
                context.signal,
            );
            return Object.fromEntries(results.map((mr) => [mr.title, new FormattedMetric(mr)] as const));
        },
        placeholderData: keepPreviousData, // show stale metrics while new metrics are loading. this is unrelated to paging.
    });
}

function toDateTimeOffset(ms?: number) {
    if (!ms) return undefined;
    return dayjs(ms).local().format("YYYY-MM-DDTHH:mm:ss.SSSZ"); // e.g. 2019-03-06T08:00:00.000-05:00
}
